// Search filter builder (Typesense; sole engine since Phase 5). The contract:
// - two access conjuncts (project membership + isPublicFor privacy scope), CNF
//   e.g. projectId:=[`a`,`b`] && isPublicFor:=[`0`, ...] && userId:=`me`
// - '' means "skip this search", never "search everything"
// isPublicFor is a string[] in the index (the numeric FEED_PUBLIC_FOR_ALL sentinel is
// stringified at index time by normalizeDocumentForTypesense), so every access id is
// compared as a string here.
import { FEED_PUBLIC_FOR_ALL } from '../Feeds/Utils/FeedsConstants'
import { DEFAULT_WORKSTREAM_ID } from '../Workstreams/WorkstreamConstants'
import {
    ARTIFACTS_INDEX_NAME_PREFIX,
    WIKI_INDEX_NAME_PREFIX,
    CHATS_INDEX_NAME_PREFIX,
    CONTACTS_INDEX_NAME_PREFIX,
    GOALS_INDEX_NAME_PREFIX,
    NOTES_INDEX_NAME_PREFIX,
    TASKS_INDEX_NAME_PREFIX,
} from './searchIndexes'

// Which record attribute carries "the user who created this object", per collection.
// These differ per object type because the underlying Firestore docs differ:
//   tasks/notes  -> userId       (the creator of the task / note)
//   goals        -> creatorId    (deliberately NOT `ownerId`: that is the assignee-scope
//                   and is the ALL_USERS sentinel on virtually every real goal)
//   contacts     -> recorderUserId (the member who added the contact; NOT `uid`, which is
//                   the contact's own identity)
//   chats/topics -> creatorId
//   artifacts    -> userId       (the owner, who is the person that created or uploaded it)
export const CREATOR_ATTRIBUTE_BY_INDEX = {
    [TASKS_INDEX_NAME_PREFIX]: 'userId',
    [GOALS_INDEX_NAME_PREFIX]: 'creatorId',
    [NOTES_INDEX_NAME_PREFIX]: 'userId',
    [CONTACTS_INDEX_NAME_PREFIX]: 'recorderUserId',
    [CHATS_INDEX_NAME_PREFIX]: 'creatorId',
    [ARTIFACTS_INDEX_NAME_PREFIX]: 'userId',
}

export const getProjectAccessIds = (loggedUser, projectId) => {
    if (loggedUser.isAnonymous) return [FEED_PUBLIC_FOR_ALL]

    const workstreamIds = loggedUser.workstreams?.[projectId]
    const projectWorkstreamIds = Array.isArray(workstreamIds) ? workstreamIds : []
    return [...new Set([FEED_PUBLIC_FOR_ALL, loggedUser.uid, DEFAULT_WORKSTREAM_ID, ...projectWorkstreamIds])]
}

// Backticks let values with special characters (ws@default, push ids) parse; a backtick
// inside a value would break out of the quoting, so it is stripped — no legitimate id
// carries one.
export const formatTypesenseValue = value => '`' + String(value).replace(/`/g, '') + '`'

export const buildTypesenseProjectsAccessFilter = (projects, loggedUser) => {
    if (!projects.length) return ''

    const projectIdsList = projects.map(project => formatTypesenseValue(project.id)).join(',')

    const accessIds = new Set()
    projects.forEach(project => {
        getProjectAccessIds(loggedUser, project.id).forEach(id => accessIds.add(String(id)))
    })
    const accessList = [...accessIds].map(formatTypesenseValue).join(',')

    return `projectId:=[${projectIdsList}] && isPublicFor:=[${accessList}]`
}

export const buildTypesenseCreatedByMeFilter = (indexPrefix, userId) => {
    if (!userId) return ''

    const creatorAttribute = CREATOR_ATTRIBUTE_BY_INDEX[indexPrefix]
    if (!creatorAttribute) return ''

    return `${creatorAttribute}:=${formatTypesenseValue(userId)}`
}

// AT-2945: what global search hands the engine so the signed-in user's own objects rank
// first even while "only what I created" is off. Same creator attribute as that filter, so
// the boost and the filter can never disagree about what "mine" means. Null means no boost:
// - the filter is on (every result is already the user's, so a boost changes nothing),
// - the wiki tab (nobody creates a wiki page; it has no creator attribute),
// - an anonymous viewer (nothing in a shared project is "theirs").
// The boost only reorders; access is still decided by buildTypesenseSearchFilters alone.
export const getCreatedByMeBoost = ({ indexPrefix, loggedUser, createdByMeOnly = false }) => {
    if (createdByMeOnly) return null
    if (!loggedUser || loggedUser.isAnonymous || !loggedUser.uid) return null
    const attribute = CREATOR_ATTRIBUTE_BY_INDEX[indexPrefix]
    if (!attribute) return null
    return { attribute, userId: loggedUser.uid }
}

export const buildTypesenseSearchFilters = ({
    indexPrefix,
    projects,
    loggedUser,
    createdByMeOnly = false,
    openTasksOnly = false,
}) => {
    const projectsAccessFilter = buildTypesenseProjectsAccessFilter(projects, loggedUser)
    if (!projectsAccessFilter) return ''

    const conjuncts = [projectsAccessFilter]

    if (indexPrefix === CONTACTS_INDEX_NAME_PREFIX) {
        conjuncts.push('isAssistant:=false')
    }

    if (createdByMeOnly) {
        // Nobody creates a wiki page: the wiki writes them all. "Only what I created" therefore
        // finds none, and the tab is skipped rather than searched unfiltered.
        if (indexPrefix === WIKI_INDEX_NAME_PREFIX) return ''
        const createdByMeFilter = buildTypesenseCreatedByMeFilter(indexPrefix, loggedUser?.uid)
        if (createdByMeFilter) conjuncts.push(createdByMeFilter)
    }

    if (openTasksOnly && indexPrefix === TASKS_INDEX_NAME_PREFIX) {
        conjuncts.push('done:=false')
    }

    return conjuncts.join(' && ')
}
